import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const SCQAProcessShowcase = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  
  // Auto-advance steps
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeStep < 3) {
        setActiveStep(activeStep + 1);
      } else {
        setActiveStep(0);
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [activeStep]);
  
  useEffect(() => {
    setIsVisible(true);
  }, []);
  
  const steps = [
    {
      letter: "S",
      title: "Situation",
      description: "Comprehensive company overview with key financial metrics and market position",
      details: [
        "Automatically retrieves latest company data and financial metrics",
        "Analyzes recent news sentiment and market trends",
        "Generates a comprehensive situation summary with key insights"
      ],
      example: "J D Wetherspoon plc (JDW.L), a key player in the consumer cyclical sector, operates within the restaurant industry. With a market capitalization of £690.26M, the company is a significant entity in the UK and Republic of Ireland's hospitality landscape. Currently priced at £631.70, the stock has experienced a 12.71% decline over the past year."
    },
    {
      letter: "C",
      title: "Complication",
      description: "Identify key challenges and issues affecting the company's performance",
      details: [
        "Generates potential complications based on financial data and market research",
        "Prioritizes complications based on potential impact to the company",
        "Provides detailed analysis of each complication with supporting evidence"
      ],
      example: "Supply Chain Disruptions: The global supply chain crisis is a significant risk for JD Wetherspoon. Disruptions could impact the company's ability to source key inputs, leading to operational inefficiencies, potential service disruptions, and increased costs."
    },
    {
      letter: "Q",
      title: "Question",
      description: "Formulate key research questions to guide your investment analysis",
      details: [
        "Generates insightful research questions based on selected complications",
        "Focuses on questions that address key investment considerations",
        "Allows customization of questions to match your specific research focus"
      ],
      example: "How might global supply chain disruptions increase operational costs for JD Wetherspoon, potentially impacting customer satisfaction, revenue generation, and ultimately the company's market valuation in the medium to long term?"
    },
    {
      letter: "A",
      title: "Hypothesis",
      description: "Develop and test investment hypotheses to reach actionable conclusions",
      details: [
        "Generates multiple investment hypotheses based on research questions",
        "Provides supporting evidence and counterarguments for each hypothesis",
        "Helps process and refine hypotheses into actionable investment conclusions",
        "Generates comprehensive research reports with your findings"
      ],
      example: "Increased Operational Costs Lead to Price Hikes: JD Wetherspoon, due to supply chain disruptions, may face increased operational costs. To maintain profitability, the company might choose to pass these costs onto consumers by increasing prices. However, this could lead to customer dissatisfaction and potentially reduce patronage."
    }
  ];
  
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };
  
  return (
    <div className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2 
            className="text-3xl font-bold text-midnight-navy mb-4"
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            variants={fadeIn}
          >
            The SCQA Research Process
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            variants={fadeIn}
            transition={{ delay: 0.1 }}
          >
            Our structured approach guides you through each step of the equity research process, ensuring thorough analysis and actionable insights.
          </motion.p>
        </div>
        
        <div className="max-w-5xl mx-auto">
          {/* Process Steps */}
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute top-10 left-0 right-0 h-1 bg-gray-200 z-0">
              <div 
                className="absolute top-0 left-0 h-full bg-electric-blue transition-all duration-500" 
                style={{ width: `${(activeStep + 1) * 25}%` }}
              ></div>
            </div>
            
            {/* Steps */}
            <div className="flex justify-between relative z-10 mb-16">
              {steps.map((step, index) => (
                <motion.div 
                  key={index}
                  className="flex flex-col items-center"
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                  variants={fadeIn}
                  transition={{ delay: index * 0.1 }}
                >
                  <div 
                    className={`w-20 h-20 rounded-full flex items-center justify-center mb-2 transition-all duration-300 ${
                      index <= activeStep 
                        ? "bg-electric-blue text-white" 
                        : "bg-white text-electric-blue border-2 border-electric-blue"
                    }`}
                  >
                    <span className="text-xl font-bold">{step.letter}</span>
                  </div>
                  <span className="font-semibold text-midnight-navy">{step.title}</span>
                </motion.div>
              ))}
            </div>
            
            {/* Process Description */}
            <motion.div 
              className="bg-white rounded-xl shadow-lg p-8 mb-8"
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              variants={fadeIn}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full bg-electric-blue text-white flex items-center justify-center mr-4">
                  <span className="text-xl font-bold">{steps[activeStep].letter}</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-midnight-navy">{steps[activeStep].title}</h3>
                  <p className="text-gray-600">{steps[activeStep].description}</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-midnight-navy mb-2">What Research Genie Does:</h4>
                <ul className="space-y-2">
                  {steps[activeStep].details.map((detail, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-midnight-navy bg-opacity-5 rounded-lg p-6 border-l-4 border-electric-blue">
                <h4 className="font-semibold text-midnight-navy mb-2">Example: JD Wetherspoon Analysis</h4>
                <p className="text-gray-700 italic">{steps[activeStep].example}</p>
              </div>
            </motion.div>
            
            {/* Navigation Dots */}
            <div className="flex justify-center space-x-2">
              {steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveStep(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === activeStep ? "bg-electric-blue" : "bg-gray-300"
                  }`}
                  aria-label={`Go to step ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SCQAProcessShowcase;
